import { useMemo, useRef, useState } from 'react';
import type { Pilot } from '@edh/shared';
import {
  MAX_GUILD_NAME,
  PLACEMENT_GAMES,
  display,
  formatRecord,
  isRanked,
  pilotRecord,
} from '@edh/shared';
import { RankedAvatar } from '../components/RankedAvatar';
import { GuildMark } from '../components/GuildMark';
import { MemberProfile } from '../components/MemberProfile';
import { Invites } from '../components/Invites';
import { LogPastGame } from '../components/LogPastGame';
import { Sheet } from '../components/Sheet';
import { AvatarError, fileToGuildIcon } from '../lib/avatar';
import { endSession, stored } from '../lib/cognito';
import { useStore } from '../lib/store';

/**
 * Administering the guild: who is in it, who leads it, and who may sign in.
 *
 * The ladder lives on Standings. This page deliberately shows no ratings — it
 * is where you come to change something, and a rating is not a thing anyone
 * should be able to change.
 */
export function Guild() {
  const { state, createPilot, isLeader } = useStore();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [showHidden, setShowHidden] = useState(false);

  const members = useMemo(
    () =>
      state.pilots
        .filter((p) => !p.retired)
        // Leaders first, then alphabetical: this is a roster, not a ranking.
        .sort((a, b) => Number(b.leader) - Number(a.leader) || a.name.localeCompare(b.name)),
    [state.pilots],
  );
  const hidden = state.pilots.filter((p) => p.retired);

  function add() {
    if (!name.trim()) return;
    void createPilot(name);
    setName('');
    setAdding(false);
  }

  return (
    <>
      <div className="section-head">
        <h2>Members</h2>
        {/* Named for what it actually makes: a player with no login. "Add"
            beside an invite panel read as a second way to do the same thing. */}
        <button
          className="btn ghost"
          style={{ padding: '4px 12px', fontSize: 13 }}
          onClick={() => setAdding(true)}
        >
          + Add guest
        </button>
      </div>

      {members.length === 0 ? (
        <div className="empty">
          <strong>No one in the guild yet</strong>
          Add the people who play. Everyone starts at 1200 and finds their level.
        </div>
      ) : (
        <div className="list">
          {members.map((pilot) => (
            <MemberAdminRow key={pilot.id} pilot={pilot} />
          ))}
        </div>
      )}

      {!isLeader && members.length > 0 && (
        <p className="hint" style={{ margin: '10px 2px 0' }}>
          Only guild leaders can remove members or grant the role. Anyone can invite.
        </p>
      )}

      {hidden.length > 0 && (
        <>
          <button
            className="btn ghost block"
            style={{ marginTop: 12 }}
            onClick={() => setShowHidden((v) => !v)}
          >
            {showHidden ? 'Hide' : 'Show'} {hidden.length} hidden
          </button>
          {showHidden && (
            <div className="list" style={{ marginTop: 8 }}>
              {hidden.map((pilot) => (
                <MemberAdminRow key={pilot.id} pilot={pilot} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Membership is who plays; access is who can sign in. Deliberately not
          linked, so a guest can be added in seconds without an account. */}
      <Invites />

      <GuildIdentity />
      <PastGame />
      <SignOut />

      {adding && (
        <Sheet title="Add a guest" onClose={() => setAdding(false)}>
          <label className="field">
            <span>Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
          </label>
          <p className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
            Someone who plays but does not sign in. They are rated like anyone else, and if
            they ever join properly they can claim this record and keep it.
          </p>
          <div className="btn-row">
            <button className="btn ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button className="btn primary" disabled={!name.trim()} onClick={add}>
              Add
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}

/**
 * A name in the roster. Everything you can read or do about a member lives one
 * tap in, on their profile — a list of people with a Remove button beside each
 * name invites a mis-tap, and email addresses down the side are noise when you
 * are looking for somebody.
 */
function MemberAdminRow({ pilot }: { pilot: Pilot }) {
  const { state, me } = useStore();
  const [open, setOpen] = useState(false);
  const record = pilotRecord(state.matches, pilot.id);
  const isMe = me?.id === pilot.id;

  return (
    <>
      <button
        className="list-item"
        style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
        onClick={() => setOpen(true)}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0, flex: 1 }}>
          <RankedAvatar pilot={pilot} size={36} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ opacity: pilot.retired ? 0.5 : 1 }}>{pilot.name}</span>
              {pilot.leader && <span className="pip gold">leader</span>}
              {/* Nobody has signed in as this record, so it is still claimable. */}
              {!pilot.sub && !pilot.retired && <span className="pip">guest</span>}
              {isMe && <span className="pip">you</span>}
            </span>
            {/* Their rating, or how far through placements they are — which is
                what somebody scanning this list is actually looking for. It
                used to be a deck count, a number nobody has ever gone to the
                guild screen to find out. */}
            <span className="sub num" style={{ display: 'block' }}>
              {formatRecord(record)}
              <span className="dot"> · </span>
              {isRanked(pilot.placementGames)
                ? display(pilot.elo)
                : `${pilot.placementGames}/${PLACEMENT_GAMES}`}
            </span>
          </span>
        </span>
        <span className="row-more" aria-hidden="true">
          ›
        </span>
      </button>

      {open && <MemberProfile pilot={pilot} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The guild's own name and mark. Leaders only — renaming it renames it for
 * everyone, so the server checks the role as well as this component.
 */
function GuildIdentity() {
  const { state, isLeader, setSettings } = useStore();
  const [name, setName] = useState(state.settings.guildName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  if (!isLeader) return null;

  const dirty = name.trim() !== state.settings.guildName;

  async function save(patch: { guildName?: string; guildIcon?: string | null }) {
    setBusy(true);
    setError(null);
    try {
      await setSettings(patch);
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not save that');
    } finally {
      setBusy(false);
    }
  }

  async function pickIcon(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      await save({ guildIcon: await fileToGuildIcon(file) });
    } catch (err) {
      setError(err instanceof AvatarError ? err.message : 'could not use that image');
    }
  }

  return (
    <>
      <div className="section-head">
        <h2>Guild identity</h2>
        {saved && <span className="hint">saved</span>}
      </div>
      <div className="panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <GuildMark name={state.settings.guildName} src={state.settings.guildIcon} size={64} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              className="btn ghost"
              style={{ padding: '7px 14px', fontSize: 14 }}
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {state.settings.guildIcon ? 'Change icon' : 'Add an icon'}
            </button>
            {state.settings.guildIcon && (
              <button
                className="btn ghost"
                style={{ padding: '7px 14px', fontSize: 13, color: 'var(--muted)' }}
                disabled={busy}
                onClick={() => void save({ guildIcon: null })}
              >
                Remove
              </button>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => void pickIcon(e.target.files?.[0])}
          />
        </div>

        <label className="field" style={{ marginBottom: dirty ? 12 : 0 }}>
          <span>Guild name</span>
          <input
            className="input"
            value={name}
            maxLength={MAX_GUILD_NAME}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && dirty) void save({ guildName: name.trim() });
            }}
          />
        </label>

        {dirty && (
          <div className="btn-row">
            <button className="btn ghost" onClick={() => setName(state.settings.guildName)}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={busy || !name.trim()}
              onClick={() => void save({ guildName: name.trim() })}
            >
              {busy ? <span className="spinner" /> : 'Rename guild'}
            </button>
          </div>
        )}

        {error && <p className="error">{error}</p>}
      </div>
    </>
  );
}

/**
 * Recording a game nobody had the app open for.
 *
 * Here rather than on Play, and leaders only. It is the one route into the
 * ratings that skips the counter, so every number on it is somebody's memory —
 * which is fine when it is the night's organiser reconstructing a game, and less
 * fine as a button anyone can reach two taps from sitting down to play.
 */
function PastGame() {
  const { isLeader } = useStore();
  const [open, setOpen] = useState(false);
  if (!isLeader) return null;

  return (
    <>
      <div className="section-head">
        <h2>Games</h2>
        {open && (
          <button className="btn ghost" onClick={() => setOpen(false)}>
            Close
          </button>
        )}
      </div>
      {open ? (
        <LogPastGame onDone={() => setOpen(false)} />
      ) : (
        <button className="btn ghost block" onClick={() => setOpen(true)}>
          Log a game already played
        </button>
      )}
    </>
  );
}

/*
 * There used to be a League settings panel here, with switches for margin of
 * victory and the progress bonus.
 *
 * Both are how this league scores games, and both were settled after a lot of
 * argument about the arithmetic behind them. A switch invites somebody to flip
 * one halfway through a season, which would silently change what every
 * subsequent game is worth without changing any game already logged — a ladder
 * measuring two different things at once, and no record on it of which was
 * which. They are built in now.
 *
 * The stored fields survive on the settings row and are simply not read; the
 * engine is told they are on. Nothing to migrate, and nothing to explain to a
 * device that has an older copy cached.
 */

/**
 * Signing out is the last thing on the screen, because it is the last thing
 * anyone wants to do by accident on game night.
 */
function SignOut() {
  const { online, queued } = useStore();
  const [confirming, setConfirming] = useState(false);
  const session = stored();
  if (!session) return null;

  return (
    <>
      <div className="section-head">
        <h2>Session</h2>
        {/*
          The sync light, moved here from the header.

          Above every screen it was a green dot that was on all evening, which
          is a light nobody reads. Here it is beside the one action its answer
          changes: whether to sign out now or wait a moment.
        */}
        <span className={`status-dot ${online ? 'online' : 'offline'}`}>
          {queued > 0 ? `${queued} queued` : online ? 'synced' : 'offline'}
        </span>
      </div>
      <div className="panel">
        <p className="hint" style={{ margin: '0 0 12px' }}>
          Signed in
          {session.username && session.username !== 'google' ? ` as ${session.username}` : ' with Google'}.
        </p>

        {/* Unsynced games live only on this device. Signing out does not delete
            them, but it does hand the phone to someone who cannot flush them. */}
        {queued > 0 && (
          <div className="banner">
            {queued} change{queued === 1 ? '' : 's'} still waiting to sync. Stay online for a
            moment before signing out.
          </div>
        )}

        {confirming ? (
          <div className="btn-row">
            <button className="btn ghost" onClick={() => setConfirming(false)}>
              Stay
            </button>
            <button className="btn danger" onClick={endSession}>
              Sign out
            </button>
          </div>
        ) : (
          <button className="btn ghost block" onClick={() => setConfirming(true)}>
            Sign out
          </button>
        )}
      </div>
    </>
  );
}
